import { AlertTriangle, Images, Package, Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CachedImage } from '~/components/common/cached-image/cached-image';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '~/components/ui/tooltip';
import { useProductCategoryDisplay } from '~/features/catalog-operations/hooks/use-product-category-display';
import {
  getProductField,
  getProductImage,
  getScoreGrade,
} from '~/features/explorer/utils/products-catalog';
import { cn } from '~/utils/cn';

import { ProductAvailabilityChip } from './product-availability-chip';
import { SourceStatusChip } from './source-status-chip';

type Product = Record<string, unknown>;

/** Safely coerces a loose (unknown) row value to a display string. */
function toStr(v: unknown): string {
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean' || typeof v === 'bigint') return String(v);
  return '';
}

/** Counts how many images a product row exposes across the common shapes, so the
 *  photo-count badge is shown only when there's genuinely more than one. */
function countImages(product: Product): number {
  for (const key of ['optimizedImages', 'optimized_images', 'images']) {
    const v = product[key];
    if (Array.isArray(v)) return v.length;
  }
  return 0;
}

interface ProductCardProps {
  product: Product;
  canEdit: boolean;
  canDelete: boolean;
  onView: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

/**
 * Product tile in the Catalog Browse language: inset image with a photo-count
 * badge and hover edit/delete, title with its id underneath, brand, a row of
 * attribute chips (colour / category / condition / quality grade), and an
 * eligibility-status + stock line. Renders only fields that exist on the row —
 * the id, eligibility badge, and photo count appear solely when present.
 */
export function ProductCard({
  product,
  canEdit,
  canDelete,
  onView,
  onEdit,
  onDelete,
}: ProductCardProps) {
  const { t } = useTranslation('explorer');

  const image = getProductImage(product);
  const title = getProductField(product, 'title', 'name');
  const vendor = (toStr(product.vendor) || toStr(product.brand)).trim();
  const sku = getProductField(product, 'sku', 'offerId', 'externalId', 'mpn');

  const color = (toStr(product.color) || toStr(product.colour)).trim();
  const category = (
    toStr(product.category) ||
    toStr(product.productType) ||
    toStr(product.product_type) ||
    toStr(product.googleProductCategory)
  ).trim();
  // A numeric GPC id becomes its readable name; a text category/type passes through.
  const { display: categoryDisplay } = useProductCategoryDisplay(category);
  const condition = toStr(product.condition).trim();

  // Stock availability — rendered via the shared ProductAvailabilityChip so the
  // card, table, and preview surfaces stay visually consistent.
  const availability = toStr(product.availability);

  // Merchant-Center eligibility (approved / disapproved / pending), shown in
  // place of the category — which already appears as an attribute chip above.
  const sourceStatus = (toStr(product.sourceStatus) || toStr(product.source_status)).trim();
  const imageCount = countImages(product);

  const rawScore = product.dataQualityScore ?? product.data_quality_score;
  const qualityScore = rawScore != null ? Number(rawScore) : null;
  const grade = qualityScore != null && !isNaN(qualityScore) ? getScoreGrade(qualityScore) : null;

  const criticalIssues = Number(product.criticalIssues ?? product.critical_issues ?? 0);

  // Attribute chips shown under the brand — only the ones that actually exist.
  const chips = [color, category, condition].filter((c) => c && c !== '—');

  return (
    <div
      role="button"
      tabIndex={0}
      className="group bg-card focus-visible:ring-ring flex cursor-pointer flex-col overflow-hidden rounded-xl border shadow-sm transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:outline-none"
      onClick={onView}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onView();
        }
      }}
    >
      {/* Inset image with overlays */}
      <div className="p-3">
        <div className="bg-muted relative aspect-square overflow-hidden rounded-lg">
          {image ? (
            <CachedImage
              src={image}
              alt={title}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <Package className="text-muted-foreground/30 h-10 w-10" aria-hidden="true" />
            </div>
          )}

          {/* Photo-count badge — bottom-right, only when >1 image exists */}
          {imageCount > 1 && (
            <span className="bg-foreground/70 absolute right-2 bottom-2 flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[10px] font-semibold text-white backdrop-blur-sm">
              <Images className="h-3 w-3" aria-hidden="true" />
              {imageCount}
            </span>
          )}

          {/* Hover actions — top-right */}
          {(canEdit || canDelete) && (
            <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100 focus-within:opacity-100">
              {canEdit && (
                <Button
                  size="icon"
                  variant="secondary"
                  className="bg-background/90 h-7 w-7 cursor-pointer shadow-sm backdrop-blur-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  aria-label={t('products.editProduct')}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              )}
              {canDelete && (
                <Button
                  size="icon"
                  variant="secondary"
                  className="bg-background/90 text-error hover:bg-error h-7 w-7 cursor-pointer shadow-sm backdrop-blur-sm hover:text-white"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  aria-label={t('products.deleteProduct')}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Title, with the id on its own line beneath */}
      <div className="px-3">
        <div className="flex min-w-0 items-center gap-1">
          {criticalIssues > 0 && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <AlertTriangle
                    className="text-error h-3.5 w-3.5 shrink-0 cursor-default"
                    aria-hidden="true"
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[200px] text-center">
                  {t('products.criticalTooltip', { count: criticalIssues })}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <p className="text-foreground truncate text-sm leading-5 font-semibold">{title}</p>
        </div>
        {sku && sku !== '—' && (
          <p className="text-muted-foreground mt-0.5 truncate font-mono text-[11px] leading-4">
            {sku}
          </p>
        )}
      </div>

      {/* Brand */}
      {vendor && <p className="text-muted-foreground truncate px-3 pt-0.5 text-xs">{vendor}</p>}

      {/* Attribute chips */}
      {(chips.length > 0 || grade) && (
        <div className="flex flex-wrap items-center gap-1.5 px-3 pt-2">
          {color && color !== '—' && (
            <span className="border-border text-muted-foreground inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] font-medium capitalize">
              {typeof CSS !== 'undefined' && CSS.supports('color', color.toLowerCase()) && (
                <span
                  className="border-border/60 h-2 w-2 rounded-full border"
                  style={{ backgroundColor: color.toLowerCase() }}
                  aria-hidden="true"
                />
              )}
              {color}
            </span>
          )}
          {[categoryDisplay, condition]
            .filter((c) => c && c !== '—')
            .map((c) => (
              <span
                key={c}
                className="bg-muted text-muted-foreground inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-medium capitalize"
              >
                {c}
              </span>
            ))}
          {grade && (
            <Badge
              variant="outline"
              className={cn(
                'pointer-events-none rounded-md px-1.5 py-0 text-[10px] leading-4 font-semibold',
                grade.className,
              )}
            >
              {grade.label}
            </Badge>
          )}
        </div>
      )}

      {/* Eligibility status + stock */}
      <div className="mt-auto flex items-center justify-between gap-2 px-3 pt-3 pb-3">
        {sourceStatus ? <SourceStatusChip status={sourceStatus} /> : <span aria-hidden="true" />}
        <ProductAvailabilityChip availability={availability} />
      </div>
    </div>
  );
}
